// Authentication service helpers

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '../types/user';

const googleProvider = new GoogleAuthProvider();

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Create user document in Firestore
  await setDoc(doc(db, 'users', user.uid), {
    email: user.email,
    displayName: displayName || null,
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp(),
  });

  return user;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // Update last login
  await setDoc(
    doc(db, 'users', userCredential.user.uid),
    { lastLogin: serverTimestamp() },
    { merge: true }
  );

  return userCredential.user;
};

/**
 * Sign in with Google OAuth
 */
export const signInWithGoogle = async () => {
  const userCredential = await signInWithPopup(auth, googleProvider);
  const user = userCredential.user;

  // Check if user document exists
  const userDoc = await getDoc(doc(db, 'users', user.uid));

  if (!userDoc.exists()) {
    // Create new user document
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      displayName: user.displayName,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    });
  } else {
    // Update last login
    await setDoc(
      doc(db, 'users', user.uid),
      { lastLogin: serverTimestamp() },
      { merge: true }
    );
  }

  return user;
};

/**
 * Sign out current user
 */
export const signOut = async () => {
  await firebaseSignOut(auth);
};

/**
 * Get current user's Firestore document
 */
export const getCurrentUserData = async (userId: string): Promise<User | null> => {
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (!userDoc.exists()) {
    return null;
  }

  const data = userDoc.data();
  return {
    userId: userDoc.id,
    email: data.email,
    displayName: data.displayName,
    bio: data.bio,
    createdAt: data.createdAt?.toDate(),
    lastLogin: data.lastLogin?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  };
};

/**
 * Subscribe to authentication state changes
 */
export const subscribeToAuthState = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};
